/**
 * Media Type Selection View
 *
 * Displays available media types as large cards for user selection.
 */

import { MediaType, MediaTypeCard } from '../types';
import { DEFAULT_MEDIA_TYPE_CARDS } from '../utils';

export interface MediaTypeSelectionProps {
  /** Available media types to display */
  enabledMediaTypes?: MediaType[];
  /** Custom media type configurations */
  mediaTypeCards?: MediaTypeCard[];
  /** Callback when a media type is selected */
  onSelectMediaType: (mediaType: MediaType) => void;
  /** Custom CSS class */
  className?: string;
}

export const MediaTypeSelection = ({
  enabledMediaTypes,
  mediaTypeCards = DEFAULT_MEDIA_TYPE_CARDS,
  onSelectMediaType,
  className = '',
}: MediaTypeSelectionProps) => {
  // Filter cards based on enabled media types
  const availableCards = mediaTypeCards.filter(card =>
    card.isEnabled && (!enabledMediaTypes || enabledMediaTypes.includes(card.type))
  );

  const handleCardClick = (mediaType: MediaType) => {
    onSelectMediaType(mediaType);
  };

  return (
    <div className={`media-type-selection ${className}`}>
      <h1 className="media-type-selection__title">Select Media Type</h1>
      <p className="media-type-selection__subtitle">
        Choose the type of media you'd like to browse or upload to your project.
      </p>

      <div className="media-type-grid">
        {availableCards.map((card) => (
          <MediaTypeCardComponent
            key={card.type}
            card={card}
            onClick={() => handleCardClick(card.type)}
          />
        ))}
      </div>
    </div>
  );
};

interface MediaTypeCardComponentProps {
  card: MediaTypeCard;
  onClick: () => void;
}

const MediaTypeCardComponent = ({ card, onClick }: MediaTypeCardComponentProps) => {
  const handleClick = () => {
    onClick();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className="media-type-card"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Select ${card.title}`}
    >
      <div
        className="media-type-card__preview"
        style={{ backgroundImage: `url(${card.previewImage})` }}
        aria-hidden="true"
      />
      <h3 className="media-type-card__title">{card.title}</h3>
      <p className="media-type-card__description">{card.description}</p>
    </div>
  );
};